<template>
  <RydHeader :title="t('car.page.title')" 
             backBtnVisible
             :backButtonName="t('a11y.cta_back_to_profile')"
             @backButtonClick="$router.replace('/cars')"
  />

  <RydLoadingOverlay v-if="!componentReady || isLoading" />
  <RydErrorFull v-if="getVehicleError" 
                :title="$t('errors.generic.title_1')"
                hasButton
                :buttonText="$t('cars.return_to_list')"
                :description="$t('cars.API_error')"
                @buttonClick="$router.replace('/cars')"
  />
  <div v-else class="f-roboto view-block py-6 px-4">
    <div class="flex flex-col gap-y-2">
      <RydInputText id="carName" 
                    v-model:valueModel="carModelForm.name" 
                    name="carName" :label="t('car.data.name')" 
                    :placeholder="t('car.data.name_placeholder')"
                    :disabled="readOnly"
      />
      <div class="fs-13 block mb-1 font-bold">
        {{ t('car.data.vehicle_type_description') }}
      </div>
      <div class="flex">
        <RydChipRadio id="vehicleCar"
                      v-model="vehicleType"
                      icon="symbols/car" 
                      class="w-28"
                      name="vehicleType"
                      :label="t('car.data.car')"
                      value="CAR"
                      tabindex="0"
                      :checked="vehicleType === 'CAR'"
                      @change="vehicleTypeChange('CAR')"
        />
        <RydChipRadio id="vehicleMotorcycle"
                      v-model="vehicleType"
                      icon="symbols/motorcycle" 
                      name="vehicleType"
                      class="w-28"
                      :label="t('car.data.motorcycle')" 
                      tabindex="0"
                      value="MOTORCYCLE"
                      :checked="vehicleType === 'MOTORCYCLE'"
                      @change="vehicleTypeChange('MOTORCYCLE')"
        />
      </div>
      <SearchList id="carBrand"
                  :defaultValue="carModelForm.brand"
                  name="carBrand" :label="$t('car.data.brand')"
                  :placeholder="$t('car.data.brand_placeholder')"
                  :placeholderSearch="t('car.modal.search_brands')"
                  idValue="id"
                  :options="!!brands ? brands : []"
                  :displayValue="carModelForm.brand || !!brands ? 'make' : false"
                  :disabled="!vehicleType || readOnly"
                  :a11yName="t('car.data.brand_placeholder')"
                  @itemSelected="brandSelected"
      />
      <SearchList id="carModel"
                  :defaultValue="carModelForm.model"
                  name="carModel" :label="$t('car.data.model')"
                  :placeholder="`${$t('car.data.model_placeholder')} ${!carModelForm.brand ? t('car.data.select_brand_hint') : ''}`"
                  :placeholderSearch="t('car.modal.search_models')"
                  :options="!!models ? (models as any) : []"
                  :displayValue="carModelForm.model || !!models ? 'model' : false"
                  :disabled="!models || readOnly"
                  idValue="id"
                  :a11yName="t('car.data.model_placeholder')"
                  @itemSelected="modelSelected"
      />

      <RydSelect id="carFuel" 
                 :defaultValue="carModelForm.fuel"
                 name="carFuel" 
                 displayValue="displayName"
                 idValue="fuelTypeId"
                 :label="$t('car.data.fuel')"
                 :placeholder="`${$t('car.data.fuel_placeholder')} ${!carModelForm.model ? t('car.data.select_model_hint') : ''}`"
                 :options="fuelTypes?.length > 0 ? fuelTypes : []"
                 :disabled="(!carModelForm.model || fuelTypes?.length === 0) || readOnly"
                 :a11yName="t('car.data.fuel_placeholder')"
                 @update="fuelSelected"
      />

      <RydInputText id="carPlate" 
                    v-model:valueModel="carModelForm.plate" 
                    name="carPlate" :label="t('car.data.license')" 
                    :placeholder="t('car.data.license_placeholder')"
                    :disabled="readOnly"
      />

      <RydInputNumber id="carMileage" 
                      v-model:valueModel="carModelForm.mileage" 
                      name="carMileage" :label="t('car.data.mileage')" 
                      :placeholder="t('car.data.mileage_placeholder')"
      />
      <!-- Date -->
      <div class="flex flex-col">
        <label class="fs-13 block mb-1 font-bold">{{ t('car.data.inspection') }}</label>
       
        <label class="relative mb-2 h-11 w-full" for="dateTime">
          
          <input
            id="dateTime"
            v-model="carModelForm.dateTimeUtility"
            data-cy="dateTime" 
            :placeholder="t('car.data.inspection_placeholder')"
            type="date"
            step="any"
            :readOnly="readOnly"
            :class="readOnly ? 'bg-transparent pointer-events-none' : 'bg-white'"
            class="cursor-pointer 
                  h-11 w-full box-border p-3
                  border border-ryd-gray3 rounded-s
                focus:border-ryd-gray5 focus:outline-none focus:ring-1 focus:ring-ryd-gray5"
            @change="dateChange"
          />
        </label>
      </div>
      <RydSelect id="carYear" 
                 :defaultValue="carModelForm.year"
                 name="carYear" :label="t('car.data.year')"
                 :placeholder="t('car.data.year_placeholder')"
                 :options="vehicleState.vehicleInfo ? yearsArray() : []"
                 :disabled="!carModelForm.model || readOnly"
                 :a11yName="t('car.data.year_placeholder')"
                 @update="yearSelected"
      />

      <RydInputText id="carVIN" 
                    v-model:valueModel="carModelForm.vin" 
                    name="carVIN" :label="t('car.data.vin')" 
                    :placeholder="t('car.data.vin_placeholder')"
                    :disabled="readOnly"
      />
    </div>
    <RydButton v-if="!isPreferred && !isNewCar" outline class="w-full mt-6" @click="setAsActive">
      {{ t('car.data.button.action1') }}
    </RydButton>
    <RydButton v-else-if="isPreferred" link theme="primary" class="w-full mt-6" disabled>
      {{ t('car.data.already_preferred') }}
    </RydButton>
    <template v-if="!readOnly"> 
      <RydButton v-if="isNewCar" class="w-full mt-6" @click="createNewCar">
        {{ t('car.data.button.action2') }}
      </RydButton>
      <RydButton v-else class="w-full mt-6" @click="updateCar">
        {{ t('car.data.button.action2') }}
      </RydButton>

      <RydButton v-if="canBeDeleted" :link="true" theme="error" class="w-full mt-6" @click="isDeleteModal = true">
        {{ t('car.data.button.action3') }}
      </RydButton>
    </template>
    <RydModalBottom v-if="isDeleteModal" 
                    :customClass="['max-h-[430px]']" 
                    @close="isDeleteModal = false"
    >
      <section class="text-left pt-10 pb-6">
        <p class="fs-15 font-bold">
          {{ t('car.modal.confirmation.title') }}
        </p>
        <p class="fs-15 mb-6 mt-2">
          {{ t('car.modal.confirmation.text') }}
        </p>
        <div class="flex justify-space-between items-center">
          <RydButton outline theme="primary" class="w-full mr-10" @click="isDeleteModal = false">
            {{ t('car.modal.confirmation.button.action1') }}
          </RydButton>
          <RydButton theme="primary" class="w-full" @click="deleteCar">
            {{ t('car.modal.confirmation.button.action2') }}
          </RydButton>
        </div>
      </section>
    </RydModalBottom>
    <RydToast v-if="errorAPI" 
              theme="error" 
              :position="'bottom'" 
              :duration="8000" 
              class="py-4"
              @isHidden="errorAPI = false"
    >
      {{ errorAPIText || t('errors.generic.text') }}
    </RydToast>
  </div>
</template>

<script setup lang="ts">
import RydButton from '@/components/RydButton/RydButton.vue';
import RydLoadingOverlay from '@/components/RydLoading/RydLoadingOverlay.vue';
import RydInputText from '@/components/RydInputText/RydInputText.vue';
import RydSelect from '@/components/RydSelect/RydSelect.vue';
import RydHeader from '@/components/RydHeader/RydHeader.vue';
import RydToast from '@/components/RydToast/RydToast.vue';
import RydModalBottom from '@/components/RydModalBottom/RydModalBottom.vue';
import RydInputNumber from '@/components/RydInputNumber/RydInputNumber.vue';
import RydErrorFull from '@/components/RydErrorFull.vue';
import RydChipRadio from '@/components/RydChipRadio/RydChipRadio.vue';
import SearchList from './components/SearchList.vue';

import { convertUTCDateToLocalDateNoTime } from '@/utils/utils';
import { CreateVehiclePayload, UpdateVehiclePayload, BrandsType, ModelType, FuelTypes } from '@/store/vehicle/types';

import { useAuthStore } from '@/store/auth/auth';
import { useVehicleState } from '@/store/vehicle/vehicle';
import { computed, onMounted, ref, reactive, onBeforeUnmount, ComputedRef } from 'vue';


const authState = useAuthStore();
const vehicleState = useVehicleState();
const route = useRoute();
const router = useRouter();


onBeforeUnmount(() => {
  vehicleState.resetVehicleStore();

});
const componentReady = ref(false);
const getVehicleError = ref(false);
onMounted(async () => {
  if (route.params.id === undefined) return router.push('/cars');

  // if is not new car
  if (!isNewCar.value) {
    try {
      await vehicleState.getVehicle(route.params.id as string);

      fillForm();
    } catch (error) {
      console.log(error);
      // If the car is not found
      getVehicleError.value = true;
      componentReady.value = true;
      return;
    }
  }

  if (!readOnly.value) {
    // Load brands on mounted
    try {
      await vehicleState.getBrands(vehicleType.value || 'CAR');

    } catch (error) {
      console.log('get brands error', error);
      getVehicleError.value = true;
      componentReady.value = true;
      return;
    }

    // If model exist, load all models and set this as the selected one and set the fuel type
    if (car.value?.ymme?.modelId) {
      await loadAndSetModelFuel();
    }
  }
  componentReady.value = true;

});

const readOnly = computed(() => vehicleState.vehicle?.readOnly ?? false);
const isNewCar = computed(() => route.params.id === 'new');

const vehicleType = ref(null);

const vehicleTypeChange = async (value: string) => {
  !!value && await vehicleState.getBrands(vehicleType.value || 'CAR');

  if (isNewCar.value) {
    carModelForm.brand = null;
    carModelForm.model = null;
    carModelForm.fuel = null;
    carModelForm.year = null;
    return;
  }

  if (value !== car.value.type.toUpperCase()) {
    // reset all field to load the new vehicle type
    carModelForm.brand = null;
    carModelForm.model = null;
    carModelForm.fuel = null;
    carModelForm.year = null;
  } else {
    // If the user change the vehicle type to the original one, we need to restore the values
    carModelForm.brand = getBrand.value;
    carModelForm.model = getModel.value;
    carModelForm.fuel = fuelTypes.value?.find(fuelType => fuelType.fuelTypeId === car.value.ymme?.fuelTypeId) || null;
    carModelForm.year = car.value.ymme?.year;
  }

};


const getBrand = computed(() => vehicleState.brands?.find(brand => brand.id === car.value.ymme?.makeId));
const getModel = computed(() => vehicleState.models?.find(model => model.id === car.value.ymme?.modelId));

const brands = computed(() => {
  if (vehicleType.value === null) return;

  return vehicleState.brands;
});

const models = computed(() => {
  if (vehicleType.value === null || !carModelForm.brand) return;

  return vehicleState.models;
});

const fuelTypes: ComputedRef<FuelTypes[]> = computed(() => carModelForm.model?.fuelTypes);
const car = computed(() => vehicleState.vehicle);
const currentCar = computed(() => authState.preferredVehicle);

const isPreferred = computed(() => route.params.id === currentCar.value?.thingId);

const carModelForm: Record<string, any> = reactive({
  name: null,
  brand: null,
  model: null,
  fuel: null,
  year: null,
  plate: null,
  mileage: null,
  nextInspectionDate: null,
  dateTimeUtility: null,
  vin: null,
  vehicleId: null,
});
const fillForm = () => {
  vehicleType.value = vehicleState.vehicle.type.toUpperCase() || null;
  carModelForm.name = car.value.nickName;
  carModelForm.brand = car.value.ymme?.makeId
    ? getBrand.value
    : null;
  carModelForm.model = car.value.ymme?.modelId
    ? getModel.value
    : null;
  carModelForm.fuel = car.value.ymme?.fuelTypeDisplayName
    ? { displayName: car.value.ymme?.fuelTypeDisplayName }
    : null;
  carModelForm.year = car.value.ymme?.year;
  carModelForm.plate = car.value.ymme?.licensePlate;
  carModelForm.mileage = mileageInKM.value;
  carModelForm.nextInspectionDate = car.value.status?.examinationDate;
  carModelForm.dateTimeUtility = car.value.status?.examinationDate
    ? convertUTCDateToLocalDateNoTime(car.value.status?.examinationDate)
    : null;
  carModelForm.vin = car.value.ymme?.VIN;
  carModelForm.vehicleId = car.value.ymme?.vehicleId;
};

const mileageInKM = computed(() => {
  // happens that the carOdometerM is not present at all
  if (!car.value.status.carOdometer?.distanceM) return null;

  const fromMToKM = car.value.status.carOdometer?.distanceM / 1000;
  return Number(fromMToKM.toFixed(0));
});

// select changes -----------
const brandSelected = async (brand: BrandsType) => {
  carModelForm.brand = brand;

  // Reset the year if the user change the brand
  if (car.value?.ymme?.make !== brand.make) carModelForm.year = null;

  carModelForm.model = null;
  carModelForm.fuel = null;

  vehicleState.resetModels();
  vehicleState.resetVehicleInfo();

  await loadModels(brand);

};
const modelSelected = (model: ModelType) => {
  carModelForm.model = model;
  carModelForm.fuel = null;
  carModelForm.year = null;
};
const fuelSelected = async (fuel: FuelTypes) => {
  carModelForm.fuel = fuel;
  await getUniqueVehicleInfo();
};
const yearSelected = (year: number): void => {
  carModelForm.year = year;
  if (!year) return null;
};

const loadModels = async (brand: BrandsType) => {
  try {
    await vehicleState.getModels(brand.id, vehicleType.value || 'CAR');

  } catch (error) {
    console.log(error);
  }
};
const getUniqueVehicleInfo = async () => {
  // if corrupted data from the API
  if (!carModelForm.fuel) return;

  try {
    await vehicleState.getVehicleInfo(
      carModelForm.brand.id,
      carModelForm.model.id,
      carModelForm.fuel.fuelTypeId
    );
  } catch (error) {
    console.log(error);
  }
};

const loadAndSetModelFuel = async () => {
  // Fill the brand object
  carModelForm.brand = getBrand.value;

  await loadModels(getBrand.value);
  // Fill the model obkect
  carModelForm.model = getModel.value;

  // Fill the fuel type object
  const getFuelTypeInfo = fuelTypes.value?.find((fuelType: FuelTypes) => fuelType.fuelTypeId === car.value.ymme?.fuelTypeId);
  getFuelTypeInfo
    ? carModelForm.fuel = getFuelTypeInfo
    : carModelForm.fuel = null;

  // Fill the vehicle info
  await getUniqueVehicleInfo();
  // Fill the years
  yearsArray();
};

const yearsArray = () => {
  const startingYear = carModelForm.model?.startYear;
  const endYear = carModelForm.model?.endYear;

  if (!startingYear || !endYear) return [];

  const years = [];
  for (let i = startingYear; i < endYear + 1; i++) {
    years.push(String(i));
  }

  return years;
};

// -------------

// date time picker -----------
const dateChange = (event: Event | InputEvent) => {
  (event.target as HTMLInputElement).value
    ? carModelForm.nextInspectionDate = new Date((event.target as HTMLInputElement).value).toISOString()
    : carModelForm.nextInspectionDate = null;
};
// -------------

const setAsActive = async () => {

  if (!isPreferred.value) {
    try {
      isLoading.value = true;
      await authState.patchPreferredVehicle(car.value._id);

      const carChangedEvent = new CustomEvent("webViewCarNewPreferred", { detail: { thingId: car.value._id } });

      window.dispatchEvent(carChangedEvent);

      if (window.webkit) {
        window.webkit.messageHandlers?.webViewCarNewPreferred?.postMessage(car.value._id);
      }

      router.push({ path: '/cars', query: { ...route.query, thingId: car.value._id } });

    } catch (error) {
      errorAPI.value = true;
      errorAPIText.value = t('car.error.set_as_preferred');
    } finally {
      isLoading.value = false;
    }

  }
};
const errorAPI = ref(false);
const errorAPIText = ref();
const isLoading = ref(false);
// Update
const updateCar = async () => {
  const updateCarPayload: UpdateVehiclePayload = {
    nickName: carModelForm.name ?? 'ryd car',
    ...(carModelForm.mileage && ({ odometerM: carModelForm.mileage * 1000 })),
    examinationDate: carModelForm.nextInspectionDate ?? null,
    type: vehicleType.value?.toUpperCase(),
    ymme: {
      licensePlate: carModelForm.plate ?? null,
      year: carModelForm.year ?? null,
      vehicleId: carModelForm.vehicleId ?? null,
      VIN: carModelForm.vin ?? null,
      fuelTypeId: carModelForm.fuel?.fuelTypeId ?? null,
      makeId: carModelForm.brand?.id ?? null,
      modelId: carModelForm.model?.id ?? null,
    }

  };
  try {
    isLoading.value = true;
    await vehicleState.updateVehicle((route.params.id as string), updateCarPayload);
    const event = new CustomEvent("webViewCarUpdate");
    await window.dispatchEvent(event);

    if (window.webkit) {
      window.webkit.messageHandlers?.webViewCarUpdate?.postMessage('webViewCarUpdate');
    }
    router.push('/cars');
  } catch (error) {
    errorAPI.value = true;
    errorAPIText.value = t('car.error.update');
  } finally {
    isLoading.value = false;
  }
};
// for trigger the submit on soft native keyboard
window.onKeyBoardConfirm = () => {
  if (isNewCar.value) {
    createNewCar();
  } else {
    updateCar();
  }
};

// Create
const createNewCar = async () => {

  const updateCarPayload: UpdateVehiclePayload = {
    nickName: carModelForm.name ?? 'ryd car',
    ...(carModelForm.mileage && ({ odometerM: carModelForm.mileage * 1000 })),
    ...(carModelForm.nextInspectionDate && ({ examinationDate: carModelForm.nextInspectionDate })),
    type: vehicleType.value?.toUpperCase(),
    ymme: {
      licensePlate: carModelForm.plate ?? null,
      year: carModelForm.year ?? null,
      vehicleId: carModelForm.vehicleId ?? null,
      VIN: carModelForm.vin ?? null,
      ...carModelForm.brand && ({ makeId: carModelForm.brand.id }),
      ...carModelForm.model && ({ modelId: carModelForm.model.id }),
      ...carModelForm.fuel && ({ fuelTypeId: carModelForm.fuel.fuelTypeId }),
      ...carModelForm.vehicleId && ({ vehicleId: carModelForm.vehicleId })
    }
  };
  const newCarPayload: CreateVehiclePayload = {
    nickName: carModelForm.name,
    fuelType: carModelForm.fuel?.fuelType,
    licensePlate: carModelForm.plate,
    accountId: authState.accountId
  };
  try {
    isLoading.value = true;
    await vehicleState.createVehicle(newCarPayload, updateCarPayload);
    const event = new CustomEvent("webViewCarCreate");

    await window.dispatchEvent(event);
    if (window.webkit) {
      window.webkit?.messageHandlers?.webViewCarDelete?.postMessage('webViewCarDelete');
    }
    router.push('/cars');
  } catch (error) {
    errorAPI.value = true;
    errorAPIText.value = t('car.error.create');
  } finally {
    isLoading.value = false;
  }
};

// Delete
const canBeDeleted = computed(() =>
  !isPreferred.value &&
  authState.userPreferences.length > 1 &&
  !isNewCar.value);

const isDeleteModal = ref(false);
const deleteCar = async () => {
  try {
    isLoading.value = true;
    await vehicleState.deleteVehicle(route.params.id as string, authState.userId);
    const event = new CustomEvent("webViewCarDelete");

    await window.dispatchEvent(event);
    if (window.webkit) {
      window.webkit.messageHandlers?.webViewCarDelete?.postMessage('webViewCarDelete');
    }
    router.push('/cars');

  } catch (error) {
    errorAPI.value = true;
    errorAPIText.value = t('car.error.delete');

  } finally {
    isLoading.value = false;
  }
};
</script>
<style lang="scss" scoped>



</style>
